import { useState, useRef, useEffect, useCallback } from 'react';
import { DateRangePicker as RDRPicker, createStaticRanges } from 'react-date-range';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
  isSameDay,
} from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styles from './DateRangePicker.module.css';

interface DateRangePickerProps {
  startDate: string; // ISO date string (YYYY-MM-DD)
  endDate: string;
  onRangeChange: (start: string, end: string) => void;
}

function toISO(d: Date): string {
  // Format local Y-M-D — not UTC. toISOString shifts to UTC first, so in
  // any positive-offset timezone, endOfDay(local) rolls over to tomorrow.
  // The picker runs entirely in local time; storage should match.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromISO(s: string): Date {
  // Parse YYYY-MM-DD as local midnight. `new Date('2026-03-07')` would treat
  // it as UTC midnight, which renders one day earlier in any negative-offset
  // timezone — the symmetric counterpart of the toISO hazard above.
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const staticRanges = createStaticRanges([
  {
    label: 'Today',
    range: () => {
      const now = new Date();
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    },
  },
  {
    label: 'Yesterday',
    range: () => {
      const d = subDays(new Date(), 1);
      return { startDate: startOfDay(d), endDate: endOfDay(d) };
    },
  },
  {
    label: 'This Week',
    range: () => ({
      startDate: startOfWeek(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last Week',
    range: () => ({
      startDate: startOfWeek(subDays(new Date(), 7)),
      endDate: endOfWeek(subDays(new Date(), 7)),
    }),
  },
  {
    label: 'Last 7 Days',
    range: () => ({
      startDate: startOfDay(subDays(new Date(), 6)),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last 30 Days',
    range: () => ({
      startDate: startOfDay(subDays(new Date(), 29)),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: 'This Month',
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last Month',
    range: () => ({
      startDate: startOfMonth(subMonths(new Date(), 1)),
      endDate: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  {
    label: 'Last 90 Days',
    range: () => ({
      startDate: startOfDay(subDays(new Date(), 89)),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: 'This Quarter',
    range: () => ({
      startDate: startOfQuarter(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
]);

export function DateRangePicker({ startDate, endDate, onRangeChange }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Track which props we last synced from to detect external changes
  const [lastProps, setLastProps] = useState({ startDate, endDate });
  const [tempRange, setTempRange] = useState({
    startDate: fromISO(startDate),
    endDate: fromISO(endDate),
    key: 'selection',
  });
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync temp range when props change (derive, not effect)
  if (startDate !== lastProps.startDate || endDate !== lastProps.endDate) {
    setLastProps({ startDate, endDate });
    setTempRange({
      startDate: fromISO(startDate),
      endDate: fromISO(endDate),
      key: 'selection',
    });
  }

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setTempRange({
          startDate: fromISO(startDate),
          endDate: fromISO(endDate),
          key: 'selection',
        });
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, startDate, endDate]);

  const handleSelect = useCallback(
    (ranges: Record<string, { startDate?: Date; endDate?: Date }>) => {
      const sel = ranges.selection;
      if (sel?.startDate && sel?.endDate) {
        setTempRange({
          startDate: sel.startDate,
          endDate: sel.endDate,
          key: 'selection',
        });
      }
    },
    [],
  );

  const handleApply = () => {
    onRangeChange(toISO(tempRange.startDate), toISO(tempRange.endDate));
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempRange({
      startDate: fromISO(startDate),
      endDate: fromISO(endDate),
      key: 'selection',
    });
    setIsOpen(false);
  };

  const displayLabel = (() => {
    const s = fromISO(startDate);
    const e = fromISO(endDate);
    const fmt = (d: Date) => {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    };
    return isSameDay(s, e) ? fmt(s) : `${fmt(s)} – ${fmt(e)}`;
  })();

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change date range"
        aria-expanded={isOpen}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="3" width="12" height="11" rx="1.5" />
          <path d="M2 6.5h12" />
          <path d="M5.5 1.5v3" />
          <path d="M10.5 1.5v3" />
        </svg>
        <span data-testid="date-range-label">{displayLabel}</span>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <RDRPicker
            ranges={[tempRange]}
            onChange={handleSelect}
            months={2}
            direction="horizontal"
            staticRanges={staticRanges}
            inputRanges={[]}
            rangeColors={['#17204d']}
            showDateDisplay
          />
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={handleCancel}>
              Cancel
            </button>
            <button type="button" className={styles.applyBtn} onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
